import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

/*
  Nginx가 /api 접두어를 Flask로 전달하는 현재 프로젝트 구조에 맞춤.
  실제 요청 예:
  /api/community/boards
  /api/community/posts
*/
const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const unwrap = (response) => response.data?.data ?? response.data;

const getErrorMessage = (error, fallback = '요청 처리에 실패했습니다.') =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  fallback;

const communityApi = {
  getBoards: async () => unwrap(await api.get('/boards')),
  getPosts: async (params = {}) => unwrap(await api.get('/posts', { params })),
  getPost: async (postId) => unwrap(await api.get(`/posts/${postId}`)),
  countView: async (postId) => unwrap(await api.post(`/posts/${postId}/view`)),

  createPost: async (formData) =>
    unwrap(
      await api.post('/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    ),

  updatePost: async (postId, payload) =>
    unwrap(await api.put(`/posts/${postId}`, payload)),

  deletePost: async (postId) =>
    unwrap(await api.delete(`/posts/${postId}`)),

  addImages: async (postId, files) => {
    const formData = new FormData();

    Array.from(files || []).forEach((file) => {
      formData.append('images', file);
    });

    return unwrap(
      await api.post(`/posts/${postId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  deleteImage: async (postId, imageId) =>
    unwrap(await api.delete(`/posts/${postId}/images/${imageId}`)),

  createComment: async (postId, payload) =>
    unwrap(await api.post(`/posts/${postId}/comments`, payload)),

  updateComment: async (commentId, content) =>
    unwrap(await api.put(`/comments/${commentId}`, { content })),

  deleteComment: async (commentId) =>
    unwrap(await api.delete(`/comments/${commentId}`)),

  togglePostLike: async (postId) =>
    unwrap(await api.post(`/posts/${postId}/like`)),

  toggleCommentLike: async (commentId) =>
    unwrap(await api.post(`/comments/${commentId}/like`)),

  toggleBookmark: async (postId) =>
    unwrap(await api.post(`/posts/${postId}/bookmark`)),

  getBookmarks: async (params = {}) =>
    unwrap(await api.get('/bookmarks', { params })),

  getMyPosts: async (params = {}) =>
    unwrap(await api.get('/my/posts', { params })),

  report: async (payload) =>
    unwrap(await api.post('/reports', payload)),
};

const postTypeLabel = {
  NORMAL: '일반',
  QUESTION: '질문',
  NOTICE: '공지',
  DIARY_SHARE: '영농일지',
  PRODUCT_SHARE: '상품',
};

const reportReasons = {
  SPAM: '스팸',
  ABUSE: '욕설·비방',
  OBSCENE: '음란물',
  ADVERTISEMENT: '부적절한 광고',
  FALSE_INFORMATION: '허위 정보',
  COPYRIGHT: '저작권 침해',
  ETC: '기타',
};

const formatDate = (value, detail = false) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('ko-KR', detail
    ? {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }
    : {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }
  ).format(date);
};

const Loading = ({ text = '불러오는 중입니다.' }) => (
  <div className="community-loading">
    <div className="community-spinner" />
    <span>{text}</span>
  </div>
);

const Empty = ({ children }) => (
  <div className="community-empty">{children}</div>
);

const Pagination = ({ pagination, onChange }) => {
  if (!pagination || Number(pagination.total_pages || 0) <= 1) return null;

  const current = Number(pagination.page || 1);
  const total = Number(pagination.total_pages || 1);
  const start = Math.max(1, current - 2);
  const end = Math.min(total, start + 4);
  const numbers = [];

  for (let number = start; number <= end; number += 1) {
    numbers.push(number);
  }

  return (
    <div className="community-pagination">
      <button
        type="button"
        disabled={current <= 1}
        onClick={() => onChange(current - 1)}
      >
        이전
      </button>

      {numbers.map((number) => (
        <button
          type="button"
          key={number}
          className={number === current ? 'active' : ''}
          onClick={() => onChange(number)}
        >
          {number}
        </button>
      ))}

      <button
        type="button"
        disabled={current >= total}
        onClick={() => onChange(current + 1)}
      >
        다음
      </button>
    </div>
  );
};

const PostCard = ({ post }) => (
  <Link to={`/community/posts/${post.id}`} className="community-post-card">
    {post.thumbnail_url ? (
      <img
        src={post.thumbnail_url}
        alt=""
        className="community-post-thumbnail"
      />
    ) : (
      <div className="community-post-thumbnail community-no-image">🌱</div>
    )}

    <div className="community-post-card-content">
      <div className="community-tags">
        {Number(post.is_notice) === 1 && (
          <span className="notice-tag">공지</span>
        )}
        <span>{post.board_name}</span>
        <span>{postTypeLabel[post.post_type] || post.post_type}</span>
      </div>

      <h3>
        {Number(post.is_secret) === 1 ? '🔒 ' : ''}
        {post.title}
      </h3>

      <div className="community-meta">
        <span>{post.member_id}</span>
        <span>{formatDate(post.created_at)}</span>
      </div>

      <div className="community-stats">
        <span>조회 {post.view_count || 0}</span>
        <span>좋아요 {post.like_count || 0}</span>
        <span>댓글 {post.comment_count || 0}</span>
      </div>
    </div>
  </Link>
);

const CommunityHeader = () => {
  const location = useLocation();

  return (
    <>
      <header className="community-board-header mt-2 mb-2">
        <div>
          <h1>커뮤니티</h1>
          <p>농사 정보와 스마트팜 경험을 함께 나눠보세요.</p>
        </div>
      </header>

      <nav className="community-menu">
        <Link
          to="/community"
          className={location.pathname === '/community' ? 'active' : ''}
        >
          전체 글
        </Link>
        <Link
          to="/community/bookmarks"
          className={location.pathname === '/community/bookmarks' ? 'active' : ''}
        >
          북마크
        </Link>
        <Link
          to="/community/my-posts"
          className={location.pathname === '/community/my-posts' ? 'active' : ''}
        >
          내 글
        </Link>

        {location.pathname !== '/community/write' && (
          <Link to="/community/write" className="community-primary-button">
            글쓰기
          </Link>
        )}
      </nav>
    </>
  );
};

const CommunityList = ({ mode = 'all' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedBoardCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('board') || '';
  }, [location.search]);

  const [boards, setBoards] = useState([]);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [boardCode, setBoardCode] = useState(selectedBoardCode);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mode !== 'all') return;

    setBoardCode(selectedBoardCode);
    setPage(1);
  }, [mode, selectedBoardCode]);

  useEffect(() => {
    if (mode !== 'all') return;

    communityApi.getBoards()
      .then((data) => setBoards(data.boards || []))
      .catch((error) => {
        alert(getErrorMessage(error, '게시판 목록을 불러오지 못했습니다.'));
      });
  }, [mode]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        let data;

        if (mode === 'bookmarks') {
          data = await communityApi.getBookmarks({
            page,
            per_page: 20,
          });
        } else if (mode === 'my-posts') {
          data = await communityApi.getMyPosts({
            page,
            per_page: 20,
          });
        } else {
          data = await communityApi.getPosts({
            board_code: boardCode || undefined,
            keyword: keyword || undefined,
            sort,
            page,
            per_page: 20,
          });
        }

        setPosts(data.posts || []);
        setPagination(data.pagination || null);
      } catch (error) {
        alert(getErrorMessage(error, '게시글을 불러오지 못했습니다.'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [boardCode, keyword, mode, page, sort]);

  const search = (event) => {
    event.preventDefault();
    setPage(1);
    setKeyword(keywordInput.trim());
  };

  const title =
    mode === 'bookmarks'
      ? ''
      : mode === 'my-posts'
        ? ''
        : '';

  return (
    <>
      {title && <h1 className="community-page-heading">{title}</h1>}

      {loading ? (
        <Loading />
      ) : posts.length === 0 ? (
        <Empty>
          {mode === 'bookmarks'
            ? '저장한 게시글이 없습니다.'
            : mode === 'my-posts'
              ? '작성한 게시글이 없습니다.'
              : '등록된 게시글이 없습니다.'}
        </Empty>
      ) : (
        <div className="community-post-list">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      )}

      <Pagination pagination={pagination} onChange={setPage} />

      {mode === 'all' && (
        <div className="community-toolbar">
          <form className="community-search-form" onSubmit={search}>
            <input
              type="search"
              value={keywordInput}
              onChange={(event) => setKeywordInput(event.target.value)}
              placeholder="제목 또는 내용 검색"
            />
            <button type="submit">검색</button>
          </form>

          <select
            className="community-sort-select"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
          >
            <option value="latest">최신순</option>
            <option value="popular">인기순</option>
            <option value="views">조회순</option>
            <option value="comments">댓글순</option>
          </select>
        </div>
      )}
    </>
  );
};

const CommentItem = ({ comment, onReload, onReply }) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(comment.content || '');
  const [saving, setSaving] = useState(false);

  const updateComment = async () => {
    if (!content.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    try {
      setSaving(true);
      await communityApi.updateComment(comment.id, content.trim());
      setEditing(false);
      await onReload();
    } catch (error) {
      alert(getErrorMessage(error, '댓글 수정에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteComment = async () => {
    if (!window.confirm('댓글을 삭제할까요?')) return;

    try {
      await communityApi.deleteComment(comment.id);
      await onReload();
    } catch (error) {
      alert(getErrorMessage(error, '댓글 삭제에 실패했습니다.'));
    }
  };

  const toggleLike = async () => {
    try {
      await communityApi.toggleCommentLike(comment.id);
      await onReload();
    } catch (error) {
      alert(getErrorMessage(error, '댓글 좋아요 처리에 실패했습니다.'));
    }
  };

  return (
    <div className={`community-comment ${comment.parent_id ? 'reply' : ''}`}>
      <div className="community-comment-head">
        <strong>{comment.member_id}</strong>
        <span>{formatDate(comment.created_at, true)}</span>
      </div>

      {editing ? (
        <div className="community-comment-edit">
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
          />
          <div>
            <button type="button" onClick={() => setEditing(false)}>
              취소
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={updateComment}
            >
              {saving ? '저장 중' : '저장'}
            </button>
          </div>
        </div>
      ) : (
        <p>{comment.content}</p>
      )}

      {comment.status !== 'DELETED' && (
        <div className="community-comment-actions">
          <button type="button" onClick={toggleLike}>
            {comment.is_liked ? '♥' : '♡'} {comment.like_count || 0}
          </button>

          {!comment.parent_id && (
            <button type="button" onClick={() => onReply(comment)}>
              답글
            </button>
          )}

          {comment.is_owner && (
            <>
              <button type="button" onClick={() => setEditing(true)}>
                수정
              </button>
              <button type="button" onClick={deleteComment}>
                삭제
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CommunityDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [replyTarget, setReplyTarget] = useState(null);
  const [savingComment, setSavingComment] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await communityApi.getPost(postId);
      setData(result);
    } catch (error) {
      alert(getErrorMessage(error, '게시글을 불러오지 못했습니다.'));
      navigate('/community');
    } finally {
      setLoading(false);
    }
  }, [navigate, postId]);

  useEffect(() => {
    communityApi.countView(postId).catch(() => {});
    load();
  }, [load, postId]);

  const comments = useMemo(() => data?.comments || [], [data]);

  const rootComments = useMemo(
    () => comments.filter((comment) => !comment.parent_id),
    [comments],
  );

  const repliesByParent = useMemo(
    () => comments.reduce((result, comment) => {
      if (comment.parent_id) {
        result[comment.parent_id] = result[comment.parent_id] || [];
        result[comment.parent_id].push(comment);
      }

      return result;
    }, {}),
    [comments],
  );

  const submitComment = async (event) => {
    event.preventDefault();

    if (!commentContent.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    try {
      setSavingComment(true);

      await communityApi.createComment(postId, {
        content: commentContent.trim(),
        parent_id: replyTarget?.id || null,
      });

      setCommentContent('');
      setReplyTarget(null);
      await load();
    } catch (error) {
      alert(getErrorMessage(error, '댓글 등록에 실패했습니다.'));
    } finally {
      setSavingComment(false);
    }
  };

  const deletePost = async () => {
    if (!window.confirm('게시글을 삭제할까요?')) return;

    try {
      await communityApi.deletePost(postId);
      navigate('/community');
    } catch (error) {
      alert(getErrorMessage(error, '게시글 삭제에 실패했습니다.'));
    }
  };

  const togglePostLike = async () => {
    try {
      await communityApi.togglePostLike(postId);
      await load();
    } catch (error) {
      alert(getErrorMessage(error, '좋아요 처리에 실패했습니다.'));
    }
  };

  const toggleBookmark = async () => {
    try {
      await communityApi.toggleBookmark(postId);
      await load();
    } catch (error) {
      alert(getErrorMessage(error, '북마크 처리에 실패했습니다.'));
    }
  };

  const reportPost = async () => {
    const reason = window.prompt(
      [
        '신고 사유 코드를 입력하세요.',
        'SPAM: 스팸',
        'ABUSE: 욕설·비방',
        'OBSCENE: 음란물',
        'ADVERTISEMENT: 광고',
        'FALSE_INFORMATION: 허위 정보',
        'COPYRIGHT: 저작권',
        'ETC: 기타',
      ].join('\n'),
      'SPAM',
    );

    if (!reason) return;

    const reasonType = reason.trim().toUpperCase();

    if (!reportReasons[reasonType]) {
      alert('올바른 신고 사유 코드를 입력해주세요.');
      return;
    }

    try {
      await communityApi.report({
        target_type: 'POST',
        target_id: Number(postId),
        reason_type: reasonType,
      });
      alert('신고가 접수되었습니다.');
    } catch (error) {
      alert(getErrorMessage(error, '신고 접수에 실패했습니다.'));
    }
  };

  if (loading) return <Loading />;
  if (!data?.post) return null;

  const { post, images = [] } = data;

  return (
    <article className="community-detail">
      <div className="community-detail-top">
        <Link to="/community">← 목록</Link>

        <div>
          {post.is_owner ? (
            <>
              <Link to={`/community/posts/${post.id}/edit`}>수정</Link>
              <button type="button" onClick={deletePost}>
                삭제
              </button>
            </>
          ) : (
            <button type="button" onClick={reportPost}>
              신고
            </button>
          )}
        </div>
      </div>

      <header className="community-detail-heading">
        <div className="community-tags">
          <span>{post.board_name}</span>
          <span>{postTypeLabel[post.post_type] || post.post_type}</span>
        </div>

        <h1>{post.title}</h1>

        <div className="community-meta">
          <strong>{post.member_id}</strong>
          <span>{formatDate(post.created_at, true)}</span>
          <span>조회 {post.view_count || 0}</span>
        </div>
      </header>

      {images.length > 0 && (
        <div className="community-image-gallery">
          {images.map((image) => (
            <img
              key={image.id}
              src={image.image_url}
              alt={image.original_name || ''}
            />
          ))}
        </div>
      )}

      <div className="community-detail-content">{post.content}</div>

      <div className="community-reaction-bar">
        <button
          type="button"
          onClick={togglePostLike}
          className={post.is_liked ? 'active' : ''}
        >
          {post.is_liked ? '♥' : '♡'} 좋아요 {post.like_count || 0}
        </button>

        <button
          type="button"
          onClick={toggleBookmark}
          className={post.is_bookmarked ? 'active' : ''}
        >
          {post.is_bookmarked ? '★' : '☆'} 북마크 {post.bookmark_count || 0}
        </button>
      </div>

      <section className="community-comments">
        <h2>댓글 {post.comment_count || 0}</h2>

        {Number(post.allow_comment) === 1 ? (
          <form className="community-comment-form" onSubmit={submitComment}>
            {replyTarget && (
              <div className="community-reply-target">
                <span>{replyTarget.member_id}님에게 답글</span>
                <button type="button" onClick={() => setReplyTarget(null)}>
                  취소
                </button>
              </div>
            )}

            <textarea
              value={commentContent}
              onChange={(event) => setCommentContent(event.target.value)}
              placeholder="댓글을 입력하세요."
            />

            <button type="submit" disabled={savingComment}>
              {savingComment ? '등록 중' : '댓글 등록'}
            </button>
          </form>
        ) : (
          <Empty>댓글 작성이 허용되지 않은 게시글입니다.</Empty>
        )}

        <div className="community-comment-list">
          {rootComments.map((comment) => (
            <React.Fragment key={comment.id}>
              <CommentItem
                comment={comment}
                onReload={load}
                onReply={setReplyTarget}
              />

              {(repliesByParent[comment.id] || []).map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  onReload={load}
                  onReply={setReplyTarget}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </section>
    </article>
  );
};

const initialForm = {
  board_id: '',
  title: '',
  content: '',
  post_type: 'NORMAL',
  shop_id: '',
  farm_diary_id: '',
  is_secret: false,
  allow_comment: true,
};

const CommunityForm = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(postId);

  const [boards, setBoards] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    communityApi.getBoards()
      .then((data) => {
        const result = data.boards || [];
        setBoards(result);

        setForm((current) => ({
          ...current,
          board_id: current.board_id || result[0]?.id || '',
        }));
      })
      .catch((error) => {
        alert(getErrorMessage(error, '게시판 목록을 불러오지 못했습니다.'));
      });
  }, []);

  useEffect(() => {
    if (!editing) return;

    communityApi.getPost(postId)
      .then((data) => {
        const post = data.post;

        if (!post.is_owner) {
          alert('게시글 수정 권한이 없습니다.');
          navigate(`/community/posts/${postId}`);
          return;
        }

        setForm({
          board_id: post.board_id,
          title: post.title || '',
          content: post.content || '',
          post_type: post.post_type || 'NORMAL',
          shop_id: post.shop_id || '',
          farm_diary_id: post.farm_diary_id || '',
          is_secret: Boolean(post.is_secret),
          allow_comment: Boolean(post.allow_comment),
        });

        setExistingImages(data.images || []);
      })
      .catch((error) => {
        alert(getErrorMessage(error, '게시글을 불러오지 못했습니다.'));
        navigate('/community');
      })
      .finally(() => setLoading(false));
  }, [editing, navigate, postId]);

  const changeField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const deleteExistingImage = async (imageId) => {
    if (!window.confirm('이미지를 삭제할까요?')) return;

    try {
      await communityApi.deleteImage(postId, imageId);

      setExistingImages((current) =>
        current.filter((image) => image.id !== imageId),
      );
    } catch (error) {
      alert(getErrorMessage(error, '이미지 삭제에 실패했습니다.'));
    }
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.board_id) {
      alert('게시판을 선택해주세요.');
      return;
    }

    if (!form.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    if (!form.content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    try {
      setSaving(true);

      if (editing) {
        await communityApi.updatePost(postId, {
          board_id: Number(form.board_id),
          title: form.title.trim(),
          content: form.content.trim(),
          post_type: form.post_type,
          shop_id: form.shop_id ? Number(form.shop_id) : null,
          farm_diary_id: form.farm_diary_id
            ? Number(form.farm_diary_id)
            : null,
          is_secret: form.is_secret,
          allow_comment: form.allow_comment,
        });

        if (images.length > 0) {
          await communityApi.addImages(postId, images);
        }

        navigate(`/community/posts/${postId}`);
        return;
      }

      const formData = new FormData();

      formData.append('board_id', String(form.board_id));
      formData.append('title', form.title.trim());
      formData.append('content', form.content.trim());
      formData.append('post_type', form.post_type);
      formData.append('shop_id', form.shop_id || '');
      formData.append('farm_diary_id', form.farm_diary_id || '');
      formData.append('is_secret', form.is_secret ? '1' : '0');
      formData.append('allow_comment', form.allow_comment ? '1' : '0');

      Array.from(images).forEach((file) => {
        formData.append('images', file);
      });

      const result = await communityApi.createPost(formData);
      navigate(`/community/posts/${result.post_id}`);
    } catch (error) {
      alert(getErrorMessage(error, '게시글 저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <form className="community-write-form" onSubmit={submit}>
      <h1>{editing ? '게시글 수정' : '새 게시글 작성'}</h1>

      <label>
        게시판
        <select
          value={form.board_id}
          onChange={(event) => changeField('board_id', event.target.value)}
          required
        >
          <option value="">게시판 선택</option>
          {boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.board_name}
            </option>
          ))}
        </select>
      </label>

      <label>
        글 유형
        <select
          value={form.post_type}
          onChange={(event) => changeField('post_type', event.target.value)}
        >
          <option value="NORMAL">일반</option>
          <option value="QUESTION">질문</option>
          <option value="DIARY_SHARE">영농일지 공유</option>
          <option value="PRODUCT_SHARE">상품 공유</option>
        </select>
      </label>

      <label>
        제목
        <input
          type="text"
          maxLength={200}
          value={form.title}
          onChange={(event) => changeField('title', event.target.value)}
          required
        />
      </label>

      <label>
        내용
        <textarea
          className="community-content-editor"
          value={form.content}
          onChange={(event) => changeField('content', event.target.value)}
          required
        />
      </label>

      <div className="community-inline-fields">
        <label>
          상점 ID
          <input
            type="number"
            min="1"
            value={form.shop_id}
            onChange={(event) => changeField('shop_id', event.target.value)}
            placeholder="선택사항"
          />
        </label>

        <label>
          영농일지 ID
          <input
            type="number"
            min="1"
            value={form.farm_diary_id}
            onChange={(event) => changeField('farm_diary_id', event.target.value)}
            placeholder="선택사항"
          />
        </label>
      </div>

      {existingImages.length > 0 && (
        <div className="community-existing-images">
          {existingImages.map((image) => (
            <div key={image.id}>
              <img src={image.image_url} alt="" />
              <button
                type="button"
                onClick={() => deleteExistingImage(image.id)}
              >
                삭제
              </button>
            </div>
          ))}
        </div>
      )}

      <label>
        이미지
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={(event) => {
            setImages(Array.from(event.target.files || []));
          }}
        />
      </label>

      {images.length > 0 && (
        <div className="community-file-list">
          {images.map((file) => (
            <span key={`${file.name}-${file.size}`}>
              {file.name}
            </span>
          ))}
        </div>
      )}

      <div className="community-checkboxes">
        <label>
          <input
            type="checkbox"
            checked={form.is_secret}
            onChange={(event) => changeField('is_secret', event.target.checked)}
          />
          비밀글
        </label>

        <label>
          <input
            type="checkbox"
            checked={form.allow_comment}
            onChange={(event) =>
              changeField('allow_comment', event.target.checked)
            }
          />
          댓글 허용
        </label>
      </div>

      <div className="community-form-actions">
        <button type="button" onClick={() => navigate(-1)}>
          취소
        </button>

        <button
          type="submit"
          className="community-primary-button"
          disabled={saving}
        >
          {saving ? '저장 중' : '저장'}
        </button>
      </div>
    </form>
  );
};

const CommunityPage = () => {
  const location = useLocation();
  const path = location.pathname;

  let content;

  if (path === '/community') {
    content = <CommunityList mode="all" />;
  } else if (path === '/community/write') {
    content = <CommunityForm />;
  } else if (path === '/community/bookmarks') {
    content = <CommunityList mode="bookmarks" />;
  } else if (path === '/community/my-posts') {
    content = <CommunityList mode="my-posts" />;
  } else if (/^\/community\/posts\/\d+\/edit$/.test(path)) {
    content = <CommunityForm />;
  } else {
    content = <CommunityDetail />;
  }

  return (
    <div className="community-shell">
      <style>{communityCss}</style>
      <CommunityHeader />
      <main className="community-content">{content}</main>
    </div>
  );
};

const communityCss = `
.community-shell {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 26px 0 70px;
  color: #1f2937;
}

.community-shell * {
  box-sizing: border-box;
}

.community-header,
.community-toolbar,
.community-detail-top,
.community-reaction-bar,
.community-comment-head,
.community-comment-actions,
.community-reply-target,
.community-form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.community-header {
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  background: #ffffff;
}

.community-board-header {
  padding: 8px 2px 14px;
}

.community-board-header h1 {
  margin: 0;
  color: #1f2937;
  font-size: 28px;
  font-weight: 800;
}

.community-board-header p {
  margin: 7px 0 0;
  color: #6b7280;
  font-size: 14px;
}


.community-title {
  color: #166534;
  font-size: 28px;
  font-weight: 800;
  text-decoration: none;
}

.community-header p {
  margin: 8px 0 0;
  color: #6b7280;
}

.community-primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 16px;

  background: #0d6efd;
  border: 1px solid #0d6efd;
  border-radius: 8px;

  color: #fff !important;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  cursor: pointer;
  transition: all .2s ease;
}

.community-primary-button:hover {
  background: #0b5ed7;
  border-color: #0a58ca;
  color: #fff !important;
}

.community-primary-button:active {
  background: #0a58ca;
  border-color: #0a53be;
}

.community-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.community-primary-button {
  margin-left: auto;
}

.community-menu a {
  padding: 10px 14px;
  border-radius: 10px;
  color: #4b5563;
  font-weight: 700;
  text-decoration: none;
  transition: background .2s;
}

.community-menu a:hover {
  background: #f5f5f5;
}

.community-menu a.active {
  background: #eee;
  color: #222;
}

.community-toolbar {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: stretch;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
}

.community-search-form {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
}

.community-search-form input {
  min-width: 0;
  flex: 1 1 auto;
}

.community-sort-select {
  width: 130px !important;
  flex: 0 0 130px;
}

.community-toolbar input,
.community-toolbar select,
.community-write-form input,
.community-write-form select,
.community-write-form textarea,
.community-comment-form textarea,
.community-comment-edit textarea {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  font: inherit;
}

.community-search-form button {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 0 18px;
  border: 0;
  border-radius: 10px;
  background: #f3f4f6;
  color: #374151;
  font-weight: 700;
  cursor: pointer;
}

.community-post-list {
  display: grid;
  gap: 12px;
}

.community-post-card {
  display: flex;
  gap: 16px;
  min-width: 0;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  color: inherit;
  text-decoration: none;
}

.community-post-thumbnail {
  width: 130px;
  height: 100px;
  flex: 0 0 130px;
  border-radius: 12px;
  background: #f3f4f6;
  object-fit: cover;
}

.community-no-image {
  display: grid;
  place-items: center;
  font-size: 34px;
}

.community-post-card-content {
  min-width: 0;
  flex: 1;
}

.community-post-card-content h3 {
  margin: 9px 0;
  overflow: hidden;
  font-size: 19px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.community-tags,
.community-meta,
.community-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #6b7280;
  font-size: 13px;
}

.community-tags span {
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
}

.community-tags .notice-tag {
  background: #fee2e2;
  color: #b91c1c;
}

.community-stats {
  margin-top: 12px;
}

.community-loading,
.community-empty {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px dashed #d1d5db;
  border-radius: 16px;
  color: #6b7280;
}

.community-spinner {
  width: 25px;
  height: 25px;
  border: 3px solid #d1fae5;
  border-top-color: #15803d;
  border-radius: 50%;
  animation: community-spin 0.8s linear infinite;
}

@keyframes community-spin {
  to { transform: rotate(360deg); }
}

.community-pagination {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding-top: 24px;
}

.community-pagination button {
  min-width: 38px;
  height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  background: #ffffff;
  cursor: pointer;
}

.community-pagination button.active {
  border-color: #0d6efd;
  background: #0d6efd;
  color: #ffffff;
}

.community-pagination button:disabled {
  opacity: 0.45;
  cursor: default;
}

.community-detail,
.community-write-form {
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #ffffff;
}

.community-detail-top a,
.community-detail-top button {
  border: 0;
  background: transparent;
  color: #4b5563;
  text-decoration: none;
  cursor: pointer;
}

.community-detail-top > div {
  display: flex;
  gap: 12px;
}

.community-detail-heading {
  padding: 28px 0 20px;
  border-bottom: 1px solid #e5e7eb;
}

.community-detail-heading h1 {
  margin: 14px 0;
  font-size: clamp(26px, 4vw, 38px);
}

.community-image-gallery {
  display: grid;
  gap: 12px;
  padding-top: 24px;
}

.community-image-gallery img {
  width: 100%;
  max-height: 720px;
  border-radius: 16px;
  background: #f8fafc;
  object-fit: contain;
}

.community-detail-content {
  min-height: 220px;
  padding: 30px 4px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

.community-reaction-bar {
  justify-content: center;
  padding: 18px 0 28px;
}

.community-reaction-bar button {
  padding: 11px 16px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #ffffff;
  cursor: pointer;
}

.community-reaction-bar button.active {
  border-color: #15803d;
  color: #15803d;
}

.community-comments {
  padding-top: 22px;
  border-top: 1px solid #e5e7eb;
}

.community-comment-form {
  margin: 16px 0 24px;
}

.community-comment-form textarea {
  min-height: 100px;
  resize: vertical;
}

.community-comment-form > button {
  display: block;
  margin: 10px 0 0 auto;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  background: #15803d;
  color: #ffffff;
  cursor: pointer;
}

.community-reply-target {
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f0fdf4;
  color: #166534;
}

.community-reply-target button {
  border: 0;
  background: transparent;
  cursor: pointer;
}

.community-comment {
  padding: 16px 0;
  border-top: 1px solid #f1f5f9;
}

.community-comment.reply {
  margin-left: 38px;
  padding-left: 16px;
  border-left: 3px solid #dcfce7;
}

.community-comment-head span {
  color: #9ca3af;
  font-size: 12px;
}

.community-comment p {
  margin: 12px 0;
  line-height: 1.65;
  white-space: pre-wrap;
}

.community-comment-actions {
  justify-content: flex-start;
}

.community-comment-actions button,
.community-comment-edit button {
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
}

.community-comment-edit textarea {
  min-height: 90px;
}

.community-comment-edit > div {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
}

.community-write-form {
  display: grid;
  gap: 18px;
  max-width: 850px;
  margin: 0 auto;
}

.community-write-form > label,
.community-inline-fields label {
  display: grid;
  gap: 8px;
  font-weight: 700;
}

.community-content-editor {
  min-height: 360px;
  resize: vertical;
  line-height: 1.7;
}

.community-inline-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.community-checkboxes {
  display: flex;
  gap: 18px;
}

.community-checkboxes label {
  display: flex;
  align-items: center;
  gap: 7px;
}

.community-checkboxes input {
  width: auto;
}

.community-existing-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
}

.community-existing-images > div {
  position: relative;
}

.community-existing-images img {
  width: 100%;
  height: 110px;
  border-radius: 10px;
  object-fit: cover;
}

.community-existing-images button {
  position: absolute;
  top: 6px;
  right: 6px;
  border: 0;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  cursor: pointer;
}

.community-file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.community-file-list span {
  padding: 6px 9px;
  border-radius: 8px;
  background: #f3f4f6;
  font-size: 13px;
}

.community-form-actions {
  justify-content: flex-end;
  padding-top: 10px;
}

.community-form-actions > button:first-child {
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
}

.community-page-heading {
  margin-top: 0;
}

@media (max-width: 700px) {
  .community-shell {
    width: min(100% - 20px, 1120px);
    padding-top: 12px;
  }

  .community-header {
    align-items: flex-start;
    padding: 18px;
  }

  .community-title {
    font-size: 22px;
  }

  .community-header p {
    display: none;
  }

  .community-board-header {
    padding: 4px 2px 10px;
  }

  .community-board-header h1 {
    font-size: 22px;
  }

  .community-board-header p {
    font-size: 12px;
  }

  .community-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 92px;
    gap: 6px;
    align-items: stretch;
  }

  .community-search-form {
    min-width: 0;
  }

  .community-search-form input {
    min-width: 0;
    padding-right: 9px;
    padding-left: 9px;
    font-size: 13px;
  }

  .community-search-form button {
    margin-left: 5px;
    padding: 0 12px;
    font-size: 13px;
    white-space: nowrap;
  }

  .community-sort-select {
    width: 92px !important;
    min-width: 0;
    padding-right: 7px !important;
    padding-left: 7px !important;
    font-size: 13px !important;
  }

  .community-post-card {
    gap: 12px;
    padding: 12px;
  }

  .community-post-thumbnail {
    width: 92px;
    height: 82px;
    flex-basis: 92px;
  }

  .community-post-card-content h3 {
    font-size: 16px;
  }

  .community-detail,
  .community-write-form {
    padding: 16px;
    border-radius: 14px;
  }

  .community-inline-fields {
    grid-template-columns: 1fr;
  }

  .community-comment.reply {
    margin-left: 18px;
  }
}
`;

export default CommunityPage;
